// queries/getSkills.ts
import datoCMSClient from './datoCMSClient';
import { Skill } from '../types';

const GET_SKILLS = `
{
  allSkills(orderBy: category_ASC) {
    name
    category
    description
    icon
  }
}
`;

// Updated fallback data to match your actual skills and experience
const FALLBACK_DATA: Skill[] = [
  // Programming Languages
  {
    name: "Python",
    category: "Programming Languages",
    description: "Versatile language for backend systems, data processing, and scripting automation",
    icon: "SiPython"
  },
  {
    name: "Java",
    category: "Programming Languages",
    description: "Enterprise-grade language for building robust, scalable applications",
    icon: "FaJava"
  },
  {
    name: "C",
    category: "Programming Languages",
    description: "Low-level programming for system-level development and performance optimization",
    icon: "SiC"
  },
  {
    name: "C++",
    category: "Programming Languages",
    description: "High-performance programming for complex algorithms and system applications",
    icon: "SiCplusplus"
  },
  {
    name: "TypeScript",
    category: "Programming Languages",
    description: "Strongly-typed JavaScript for building maintainable web applications",
    icon: "SiTypescript"
  },
  {
    name: "JavaScript",
    category: "Programming Languages",
    description: "Dynamic language powering modern web interfaces and server applications",
    icon: "SiJavascript"
  },
  {
    name: "SQL",
    category: "Programming Languages",
    description: "Essential for database operations, analytics, and data retrieval",
    icon: "FaDatabase"
  },
  {
    name: "Bash",
    category: "Programming Languages",
    description: "Command-line scripting for system administration and workflow automation",
    icon: "FaTerminal"
  },
  
  // Web Technologies & Frameworks
  {
    name: "Node.js",
    category: "Web Technologies",
    description: "JavaScript runtime enabling high-performance backend development",
    icon: "FaNodeJs"
  },
  {
    name: "Django",
    category: "Web Technologies",
    description: "Full-featured Python framework for secure, scalable web applications",
    icon: "SiDjango"
  },
  {
    name: "Express.js",
    category: "Web Technologies",
    description: "Lightweight Node.js framework for building RESTful APIs quickly",
    icon: "SiExpress"
  },
  {
    name: "Flask",
    category: "Web Technologies",
    description: "Minimalist Python framework perfect for microservices and APIs",
    icon: "SiFlask"
  },
  {
    name: "GraphQL",
    category: "Web Technologies",
    description: "Modern API technology for efficient data fetching and manipulation",
    icon: "SiGraphql"
  },
  {
    name: "React",
    category: "Web Technologies",
    description: "Component-based library for creating interactive user experiences",
    icon: "FaReact"
  },
  
  // Data Storage
  {
    name: "PostgreSQL",
    category: "Data Storage",
    description: "Advanced open-source database with powerful querying capabilities",
    icon: "SiPostgresql"
  },
  {
    name: "MongoDB",
    category: "Data Storage",
    description: "Flexible document database for handling unstructured data at scale",
    icon: "SiMongodb"
  },
  {
    name: "MySQL",
    category: "Data Storage",
    description: "Reliable relational database trusted by millions of applications",
    icon: "SiMysql"
  },
  {
    name: "Snowflake",
    category: "Data Storage",
    description: "Cloud-native data warehouse for analytics and data science workloads",
    icon: "SiSnowflake"
  },
  {
    name: "Redis",
    category: "Data Storage",
    description: "High-speed in-memory database for caching and real-time applications",
    icon: "SiRedis"
  },
  
  // Infrastructure & Deployment
  {
    name: "Docker",
    category: "Infrastructure & Deployment",
    description: "Containerization technology for consistent application deployment",
    icon: "FaDocker"
  },
  {
    name: "Kubernetes",
    category: "Infrastructure & Deployment",
    description: "Container orchestration platform for managing distributed applications",
    icon: "SiKubernetes"
  },
  {
    name: "AWS",
    category: "Infrastructure & Deployment",
    description: "Comprehensive cloud platform with services like Lambda, EC2, and S3",
    icon: "FaAws"
  },
  {
    name: "Jenkins",
    category: "Infrastructure & Deployment",
    description: "Automation server for building robust CI/CD pipelines",
    icon: "SiJenkins"
  },
  {
    name: "Terraform",
    category: "Infrastructure & Deployment",
    description: "Infrastructure-as-code tool for provisioning cloud resources",
    icon: "SiTerraform"
  },
  {
    name: "Azure",
    category: "Infrastructure & Deployment",
    description: "Microsoft's cloud platform for enterprise applications and services",
    icon: "SiMicrosoftazure"
  },
  
  // Development Tools
  {
    name: "Git/GitHub",
    category: "Development Tools",
    description: "Essential version control system for collaborative software development",
    icon: "FaGitAlt"
  },
  {
    name: "Linux",
    category: "Development Tools",
    description: "Open-source operating system for servers and development environments",
    icon: "FaLinux"
  },
  {
    name: "JIRA",
    category: "Development Tools",
    description: "Agile project management and issue tracking for development teams",
    icon: "SiJira"
  },
  {
    name: "Selenium",
    category: "Development Tools",
    description: "Automated testing framework for web application quality assurance",
    icon: "SiSelenium"
  }
];

export async function getSkills(): Promise<Skill[]> {
  try {
    const data = await datoCMSClient.request<{ allSkills: Skill[] }>(GET_SKILLS);
    return data.allSkills;
  } catch (error) {
    console.warn("Error fetching skills data, using fallback data:", error);
    return FALLBACK_DATA;
  }
}